import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/authService';
import { useParams, useNavigate } from 'react-router-dom';
import { surveySteps } from "./Questions/Questions";
import VideoComponent from './VideoComponent';
import QuizComponent from './QuizComponent';
import DecisionComponent from './DecisionComponent';
import CommunityForm from './CommunityForm/CommunityForm';
import useUserProgress from './userProgressTrack/useUserProgress';
import updateUserProgress from './userProgressTrack/updateUserProgress';
import WealthBuildingForm from './WealthBuildingStage/WealthBuildingForm'; // Import the WealthBuildingForm component
import MasterClass from './Masterclass/MasterClass';
import ErrorBoundary from './ErrorBoundary';

import { db } from '../../utils/firebase-config';
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import StepHeader from './StepHeader';
import { jsx } from 'react/jsx-runtime';

const SurveyContainer = () => {
  const { user } = useAuth();
  const [userId, setUserId] = useState(user ? user.uid : null);
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('currentStep');
    return savedStep ? parseInt(savedStep, 10) : 0;
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [lastDecisionStep, setLastDecisionStep] = useState(null);
  const [masterClassCompleted, setMasterClassCompleted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const { stepId } = useParams(); // Get stepId from URL
  const navigate = useNavigate();
  const [progress, setProgress] = useUserProgress(user ? user.uid : null);
  const currentStepData = surveySteps.find(step => step.id === currentStep);
  const area = surveySteps[currentStep]?.area;

  useEffect(() => {
    if (user) {
      setUserId(user.uid);
    }
  }, [user]);

  useEffect(() => {
    if (stepId) {
      setCurrentStep(parseInt(stepId, 10) || 0);
    }
  }, [stepId]);

  useEffect(() => {
    const fetchProgress = async () => {
      if (userId) {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProgress(data.progress || {});
        }
      }
    };

    fetchProgress();
  }, [userId]);

  useEffect(() => {
    if (progress && area) {
      const savedStep = progress[area] || 0;
      if (savedStep !== currentStep) {
        setCurrentStep(savedStep);
      }
    }
  }, [progress, area]);

  useEffect(() => {
    const fetchCurrentStep = async () => {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const step = userData.currentStep || 0;
        console.log(`Fetched current step from database: ${step}`);
        setCurrentStep(step);
      } else {
        console.log('No such document!');
      }
    };

    if (userId) {
      fetchCurrentStep();
    }
  }, [userId]);

  useEffect(() => {
    if (surveySteps[currentStep] && surveySteps[currentStep].questions) {
      setShuffledQuestions(surveySteps[currentStep].questions.sort(() => 0.5 - Math.random()));
    }
  }, [currentStep]);

  const updateCurrentStep = async (stepId) => {
    console.log(`Updating current step to: ${stepId}`);
    const userDocRef = doc(db, 'users', userId);
    try {
      await updateDoc(userDocRef, { currentStep: stepId });
      console.log("Current step updated successfully in Firebase!");
      navigate(`/survey/${stepId}`);
    } catch (error) {
      console.error("Error updating current step: ", error);
    }
  };

  const handleNextStep = async () => {
    const nextStepId = currentStepData.nextStep ?? surveySteps[currentStep + 1]?.id;
    if (nextStepId !== undefined) {
      try {
        console.log(`Next step ID: ${nextStepId}`);
        await updateCurrentStep(nextStepId);
        await updateUserProgress(userId, surveySteps[nextStepId].area, nextStepId);
        setCurrentStep(nextStepId);
        setCurrentQuestionIndex(0);
        setTotalQuestions(0);
        setCorrectAnswers(0);
      } catch (error) {
        console.error("Failed to move to the next step:", error);
      }
    } else {
      console.log("No next step defined.");
    }
  };

  useEffect(() => {
    console.log(`Current step: ${currentStep}`); // Log the current step whenever it changes
  }, [currentStep]);

  const handlePreviousStep = () => {
    if (currentStep !== 0) {
      const previousStep = currentStep - 1;
      updateCurrentStep(previousStep);
      setCurrentStep(previousStep);
    }
  };

  const resetSurvey = async () => {
    await updateCurrentStep(0);
    setCurrentStep(0);
    setCurrentQuestionIndex(0);
    setTotalQuestions(0);
    setCorrectAnswers(0);
  };

  useEffect(() => {
    const fetchCompletionStatus = async () => {
      if (userId) {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.completedSteps && data.completedSteps.includes(currentStep)) {
            console.log("Step already completed.");
            const nextAvailableStep = data.completedSteps.sort().pop() + 1;
            setCurrentStep(nextAvailableStep);
          }
        }
      }
    };

    fetchCompletionStatus();
  }, [userId, currentStep]);

  useEffect(() => {
    const fetchMasterClassStatus = async () => {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMasterClassCompleted(!!data.masterclassCompleted);
      } else {
        console.log("User document does not exist, initializing...");
        await setDoc(docRef, { masterclassCompleted: false });
        setMasterClassCompleted(false);
      }
    };

    fetchMasterClassStatus();
  }, [userId]);

  const handleQuizCompletion = (selectedOption, questionId) => {
    setSelectedOptions(prevOptions => ({
      ...prevOptions,
      [questionId]: selectedOption
    }));

    const currentQuestions = shuffledQuestions;
    const isCorrect = selectedOption === currentQuestions[currentQuestionIndex].correctAnswer;

    setTotalQuestions(prev => prev + 1);
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
      if (currentStep === 0) {
        setMasterClassCompleted(true);
      }
    }
  };

  const handleNextQuestionWithAnswer = (selectedOption) => {
    const question = shuffledQuestions[currentQuestionIndex];
    const isCorrect = selectedOption === question.correctAnswer;
  
    setSelectedOptions(prevOptions => ({
      ...prevOptions,
      [question.id]: selectedOption
    }));
  
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    }
  
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Added new function to handle previous question navigation
  // const handlePreviousQuestion = () => {
  //   if (currentQuestionIndex > 0) {
  //     setCurrentQuestionIndex(currentQuestionIndex - 1);
  //   }
  // };

  const moveToNextStep = async () => {
    const nextStepIndex = currentStep + 1;
    if (nextStepIndex < surveySteps.length) {
      try {
        await updateCurrentStep(nextStepIndex);
        await updateUserProgress(userId, surveySteps[nextStepIndex].area, nextStepIndex);
        setCurrentStep(nextStepIndex);
        setCurrentQuestionIndex(0);
        setTotalQuestions(0);
        setCorrectAnswers(0);
      } catch (error) {
        console.error("Failed to move to the next step:", error);
      }
    } else {
      console.log("No more steps available.");
    }
  };

  const handleDecisionSelection = async (nextStepId) => {
    setLastDecisionStep(currentStep);
    console.log(`Decision selected, moving to step ID: ${nextStepId}`);
    await updateCurrentStep(nextStepId);
    setCurrentStep(nextStepId);
    setCurrentQuestionIndex(0);
  };

  const handleSubmitPreWorkshop = async (e) => {
    e.preventDefault();
    const answers = {};
    const currentQuestions = surveySteps[currentStep].questions;
    const currentStepTitle = surveySteps[currentStep].stepTitle;
    const currentArea = surveySteps[currentStep].area;
    let allAnswered = true;

    answers[`preWorkshop.${currentStepTitle}`] = {};

    for (const question of currentQuestions) {
      const selectedOption = selectedOptions[question.id];
      if (!selectedOption) {
        allAnswered = false;
        break;
      }
      answers[`preWorkshop.${currentStepTitle}`][question.title] = {
        stepTitle: surveySteps[currentStep].stepTitle,
        selectedOption: selectedOption,
      };
    }

    if (!allAnswered) {
      setShowReviewPopup(true);
      setTimeout(() => {
        setShowReviewPopup(false);
      }, 2000);
      return;
    }

    try {
      await updateDoc(doc(db, "users", userId), answers);
      console.log("Pre-workshop answers saved successfully!");
      moveToNextStep();
    } catch (error) {
      console.error("Failed to save pre-workshop answers:", error);
    }
  };

  // Highlight the Questions based on correct or not answers
  const [highlightedQuestion, setHighlightedQuestion] = useState(null);
  const [highlightColor, setHighlightColor] = useState('');

  // Highlight question text based on the correctness of the answer
  const handleHighlight = (questionId, color) => {
    setHighlightedQuestion(questionId);
    setHighlightColor(color);
    setTimeout(() => {
      setHighlightedQuestion(null);
      setHighlightColor('');
    }, 2000); // Highlight for 2 seconds
  };

  // Handle Submit to database
  const handleSubmit = async (e) => {
    e.preventDefault();
    const question = shuffledQuestions[currentQuestionIndex];
    const selectedOption = selectedOptions[question.id];

    if (!selectedOption) return;

    const isCorrect = selectedOption === question.correctAnswer;
    let result;

    if (currentStep === 0) {
      // Masterclass step logic
      if (!isCorrect) {
        setShowReviewPopup(true);
        setTimeout(() => {
          setShowReviewPopup(false);
        }, 2000);
        return;
      }

      // Update masterclass completion status without storing the question in answers
      const result = { masterclassCompleted: true };
      try {
        await updateDoc(doc(db, "users", userId), result);
        //console.log("Masterclass completed successfully!");
        const nextStep = currentStep + 1;
        await updateCurrentStep(nextStep);
        moveToNextStep();
      } catch (error) {
        console.error("Failed to update masterclass completion:", error);
      }
    } else {
      const currentAnswers = (
        await (async () => {
          const docRef = doc(db, "users", userId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data().answers) {
            return docSnap.data().answers;
          } else {
            return {};
          }
        })()
      );

      const newAnswer = {
        question: question.title,
        isCorrect: isCorrect,
        questionID: question.id,
        selectedOption: selectedOption
      };

      result = {
        [`answers.${surveySteps[currentStep].area}.${surveySteps[currentStep].stepTitle}`]: {
          ...currentAnswers[surveySteps[currentStep].area]?.[surveySteps[currentStep].stepTitle],
          [`${question.title}`]: newAnswer
        }
      };
    }

    try {
      await updateDoc(doc(db, "users", userId), result);
      console.log("Document updated successfully!");

      handleHighlight(question.id, isCorrect ? 'green' : 'red');

      if (currentStep === 0) {
        // Masterclass step logic
        if (currentQuestionIndex >= shuffledQuestions.length - 1) {
          const correctAnswers = Object.values(selectedOptions).filter(
            (option, idx) => option === shuffledQuestions[idx].correctAnswer
          ).length;
  
          // Check if all questions are answered correctly
          if (correctAnswers === shuffledQuestions.length) {
            const masterclassResult = { masterclassCompleted: true };
            try {
              await updateDoc(doc(db, "users", userId), masterclassResult);
              console.log("Masterclass completed successfully!");
              await handleNextStep();
            } catch (error) {
              console.error("Failed to update masterclass completion:", error);
            }
          } else {
            setShowReviewPopup(true);
            setTimeout(() => {
              setShowReviewPopup(false);
              //resetSurvey();
            }, 3000);
          }
        } else {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
      } else {
        // Regular step logic
        if (isCorrect || currentQuestionIndex >= shuffledQuestions.length - 1) {
          await handleNextStep(); // Call handleNextStep here
        } else {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
      }
    } catch (error) {
      console.error("Failed to save answer or masterclass completion:", error);
    }
  };

  const handleJumpToArea = async (area) => {
    const savedStep = progress[area] || 0;
    setCurrentStep(savedStep);
    await updateUserProgress(userId, surveySteps[currentStep].area, currentStep); // Save the current step for the previous area
    navigate(`/survey/${savedStep}`);
  };

  return (
    <div>
      {showReviewPopup && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 bg-white border border-gray-300 shadow-lg rounded-md z-50">
          <h4 className="text-red-600">
            <strong>{currentStepData.preWorkshop ? "Please answer all questions before submitting." : "Please rewatch the Video and try again."}</strong>
          </h4>
          <p>{currentStepData.preWorkshop ? "Some of your responses were incorrect." : "Some of your responses were incorrect."}</p>
        </div>
      )}


      
      {/* Pre-workshop Questions */}
      {currentStepData.preWorkshop && (
        <>
          <StepHeader stepTitle={currentStepData.stepTitle} stepDescription={currentStepData.stepDescription} area={currentStepData.area} />
          <form onSubmit={handleSubmitPreWorkshop}>
            <div className="grid grid-cols-2 gap-4">
              {currentStepData.questions.map((question, index) => (
                <div key={index}>
                  <QuizComponent
                    question={question}
                    onComplete={handleQuizCompletion}
                    currentStep={currentStep}
                    stepTitle={currentStepData.stepTitle}
                    area={currentStepData.area}
                    isPreWorkshop={currentStepData.preWorkshop}
                    questionNumber={index + 1}  
                  />
                </div>
              ))}
            </div>
            <button type="submit" className="mt-4 px-8 py-3 text-black border border-black font-semibold rounded-full hover:bg-amber-400 transition-colors">
              Save All and Continue
            </button>
          </form>
        </>
      )}
  
      {/* Regular Quiz Questions */}
      {!currentStepData.preWorkshop && !currentStepData.decision && currentStepData.area !== 'Wealth Building Stage' && currentStepData.area !== 'Masterclass' && shuffledQuestions.length > 0 && currentStepData.id !== 19 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 grid-rows-1 sm:grid-cols-1">
            <StepHeader stepTitle={currentStepData.stepTitle} stepDescription={currentStepData.stepDescription} area={currentStepData.area} />
            <div className="">
              {currentStepData.videoUrl && <VideoComponent src={currentStepData.videoUrl} />}
              <form onSubmit={handleSubmit}>
                <QuizComponent
                  question={shuffledQuestions[currentQuestionIndex]}
                  onComplete={handleQuizCompletion}
                  currentStep={currentStep}
                  stepTitle={currentStepData.stepTitle}
                  area={currentStepData.area}
                  isPreWorkshop={currentStepData.preWorkshop}
                  highlighted={highlightedQuestion === shuffledQuestions[currentQuestionIndex].id}
                  highlightColor={highlightColor}
                  questionNumber={currentQuestionIndex + 1}  
                />
                <button type="submit" className="mt-2 px-8 py-3 text-black border border-black font-semibold rounded-full hover:bg-amber-400 transition-colors">
                  Save / Continue
                </button>
                {/* {currentStep === 0 && (
                  <button type="button" onClick={() => handleNextQuestionWithAnswer(selectedOptions[shuffledQuestions[currentQuestionIndex].id])} className="mt-2 ml-2 px-8 py-3 text-black border border-black font-semibold rounded-full hover:bg-amber-400 transition-colors">
                    Next Question
                  </button>
                )} */}
              </form>
            </div>
          </div>
        </>
      )}
  
      {/* Decision Step */}
      {currentStepData.decision && !showReviewPopup && (
        <DecisionComponent
          options={currentStepData.options}
          onSelect={(step, text) => {
            setCurrentStep(step); // Update the current step in the parent component
          }}
          currentStep={currentStep}
          userId={userId}
          progress={progress}
          setCurrentStep={setCurrentStep}
          area={currentStepData.area}
          handleNextStep={handleNextStep}
        />
      )}
  
      {/* Community Form */}
      {currentStepData.area === 'Community-Minded Business Concept Form' && (
        <CommunityForm
          currentStepData={surveySteps[currentStep]}
          userId={userId}
          handleNextStep={handleNextStep}
          currentStep={currentStep}
          handlePreviousStep={handlePreviousStep}
          setCurrentStep={setCurrentStep}
          progress={progress}
          area={currentStepData.area}
          stepTitle={currentStepData.stepTitle}
        />
      )}
  
      {/* Wealth Building Form */}
    
      {currentStepData.area === 'Wealth Building Stage' && currentStepData.id !== 21 && (
          <ErrorBoundary>
        <WealthBuildingForm 
          userId={userId}
          currentStep={currentStep}
          handleNextStep={handleNextStep}
          handlePreviousStep={handlePreviousStep}
          currentStepData={currentStepData}
          area={currentStepData.area || 'Wealth Building Stage'}
          progress={progress}
          //setCurrentStep={setCurrentStep}
          //nextStep={currentStepData.nextStep} 
        />
        </ErrorBoundary>
      )}
      


      {/* Masterclass */}
      {currentStepData.area === 'Masterclass' && (
        <MasterClass 
          userId={userId}
          currentStep={currentStep}
          handleNextStep={handleNextStep}
          handlePreviousStep={handlePreviousStep}
          currentStepData={currentStepData}
          area={currentStepData.area}
          progress={progress}
          setCurrentStep={setCurrentStep}
          nextStep={currentStepData.nextStep} 
        />
      )}
    </div>
  );
};

export default SurveyContainer;
